import { LINE_CHART_OPTIONS } from '../../common/utils/constants'
import { AxisValues, ChartElements } from '../../common/utils/enum'
import {
  applyAxisRange,
  clone,
  createDottedLineZonesForNulls,
  fetchSeriesDataWithMarker,
  findNullStartIndex,
  formatValues,
  formatNumberOnUnitMeasure,
  formatUnitMeasure,
  getSuppressedCountries,
} from '../../common/utils/helper'
import { COLOR_MAP } from '../../common/utils/theme'
import { findFirstAvailableValueFromEnd } from '../indicatorProfile/charts/common/helper'

export const lineChartOptions = (
  indicatorData = [],
  title = '',
  subtitle = '',
  isDownload = true,
  lineChartCountryDetails = [],
  countryDropdownValue,
  unitMeasureName = '',
  visualization = null,
  unitMultName = ''
) => {
  // Ensure indicatorData and lineChartCountryDetails exist
  if (!indicatorData.length || !lineChartCountryDetails.length) {
    return {}
  }

  let globalFirstValueIndex = null
  let globalLastValueIndex = null
  const unitMeasure = indicatorData[0].unitMeasure
  const suppressedCountries = visualization?.suppression
  // const suppressedCountries = 'AUT'
  let filteredCountryDetails = lineChartCountryDetails.filter(
    (item) => !getSuppressedCountries(suppressedCountries).includes(item.id)
  )
  filteredCountryDetails.forEach((detail) => {
    const countryData = indicatorData?.find(
      (item) => item?.countryCode?.toLowerCase() === detail?.id?.toLowerCase()
    )

    if (!countryData) {
      return
    }

    const yAxisData = countryData?.data?.yAxis || []

    const firstIndex = yAxisData?.findIndex(
      (element) =>
        element && element !== 'null' && element !== 'NaN' && element !== ''
    )

    const lastIndex = findNullStartIndex(yAxisData)

    if (firstIndex !== -1) {
      if (
        globalFirstValueIndex === null ||
        firstIndex < globalFirstValueIndex
      ) {
        globalFirstValueIndex = firstIndex
      }
    }

    if (lastIndex !== -1) {
      if (globalLastValueIndex === null || lastIndex > globalLastValueIndex) {
        globalLastValueIndex = lastIndex
      }
    } else {
      if (
        globalLastValueIndex === null ||
        yAxisData.length > globalLastValueIndex
      ) {
        globalLastValueIndex = yAxisData.length
      }
    }
  })

  if (globalFirstValueIndex === null) {
    globalFirstValueIndex = 0
  }
  if (globalLastValueIndex === null) {
    globalLastValueIndex = indicatorData[0]?.data?.yAxis?.length || 0
  }

  const lineData = filteredCountryDetails
    .map((detail) => {
      const countryData = indicatorData.find(
        (item) => item.countryCode.toLowerCase() === detail.id.toLowerCase()
      )
      // Handle case where countryData might be missing
      if (!countryData) {
        return {
          seriesData: {
            name: detail.name,
            data: [],
            connectNulls: true,
            zoneAxis: 'x',
            zones: [],
            color:
              detail.name?.toLowerCase() === 'world'
                ? COLOR_MAP.selected_1
                : COLOR_MAP.selected_2,
          },
          x_axisData: {
            categories: [],
          },
        }
      }

      const yAxisData = countryData?.data?.yAxis || []
      const xAxisData = countryData?.data?.xAxis || []

      const yaxisData = yAxisData?.slice(
        globalFirstValueIndex,
        globalLastValueIndex
      )
      const x_axisData = xAxisData?.slice(
        globalFirstValueIndex,
        globalLastValueIndex
      )
      const formattedData = yaxisData?.map((val) => formatValues(val)) || [] //formatNumberOnUnitMeasure(+val, unitMeasure)

      const color =
        detail.name?.toLowerCase() === 'world'
          ? COLOR_MAP.selected_1
          : COLOR_MAP.selected_2

      return {
        seriesData: {
          name: detail.name,
          data: fetchSeriesDataWithMarker(formattedData, false),
          connectNulls: true,
          color,
          zoneAxis: 'x',
          zones: createDottedLineZonesForNulls(formattedData),
        },
        x_axisData: {
          categories: x_axisData,
        },
      }
    })
    .filter(Boolean) // Filter out nulls

  const xAxisCategories =
    lineData.length > 0 ? lineData[0].x_axisData.categories : []

  let options = {
    ...LINE_CHART_OPTIONS,
    chart: {
      height: '345',
      events: {
        load() {
          const chart = this
          const selectedCountryDetail = lineChartCountryDetails.find(
            (cd) => cd?.id === countryDropdownValue
          )
          const cNameToShowTooltip = selectedCountryDetail?.name

          let mostRecentPoint = null

          chart.series.forEach((series) => {
            if (series.name === cNameToShowTooltip) {
              mostRecentPoint = findFirstAvailableValueFromEnd(series.data)
            }
          })

          if (mostRecentPoint) {
            chart.tooltip.refresh(mostRecentPoint)
          }
        },
      },
    },
    tooltip: {
      ...LINE_CHART_OPTIONS.tooltip,
      backgroundColor: null,
      borderWidth: 0,
      shadow: false,
      useHTML: true,
      style: {
        padding: 0,
      },
      formatter: function () {
        const title = `<div class="tui_chart_tooltip_title" style="font-size: 14px; font-weight: 600; line-height: 16px; color: rgba(0, 0, 0, 0.87); padding-bottom: 4px; margin-bottom: 4px; border-bottom: 1px solid #CED4DE;">${this.x}</div>`

        let points = this.points
          ?.map((point) => {
            let symbol = ''
            let symbolStyle = `font-size: 17px; color: ${point.color}; margin-right: 8px; vertical-align: middle; display: inline-block;`

            if (point.series.symbol) {
              switch (point.series.symbol) {
                case 'circle':
                  symbol = '●'
                  break
                case 'diamond':
                  symbol = '♦'
                  break
                case 'square':
                  symbol = '■'
                  break
                case 'triangle':
                  symbol = '▲'
                  break
                case 'triangle-down':
                  symbol = '▼'
                  break
                default:
                  symbol = '●'
              }

              if (
                point.series.symbol === 'triangle' ||
                point.series.symbol === 'triangle-down' ||
                point.series.symbol === 'square'
              ) {
                symbolStyle = `font-size: 12px; color: ${point.color}; margin-right: 8px; vertical-align: middle; display: inline-block;`
              }

              symbol = `<span style="${symbolStyle}">${symbol}</span>`
            } else if (point.series.marker && point.series.marker.symbol) {
              const url = point.series.marker.symbol.replace(/^url\(|\)$/g, '')
              symbol = `<img src="${url}" alt="Marker" style="height: 10px; width: 10px; margin-right: 8px; vertical-align: middle;" />`
            }

            return `
          <li style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 4px;">
            <div class="tooltip_country" style="display: flex; align-items: center; min-width: 150px;">
              ${symbol}
              ${point.series.name}
            </div>
            <span class="tooltip_value" style="font-size: 16px; font-weight: 700; line-height: 20px; color: rgba(0, 0, 0, 0.87);">
              ${formatNumberOnUnitMeasure(
                point.y,
                unitMeasure,
                indicatorData[0]?.decimals
              )} ${unitMultName} ${formatUnitMeasure(
              unitMeasure,
              unitMeasureName
            )}
            </span>
          </li>
        `
          })
          .join('')

        return `
      <div class="tui_chart_tooltip" style="border: 0.5px solid #CED4DE; background: #FFF; padding: 6px 8px; box-shadow: 4px 4px 4px 0px rgba(0, 0, 0, 0.15); width: auto;">
        ${title}
        <ul style="padding-left: 0; list-style: none; margin: 0;">
          ${points}
        </ul>
      </div>
    `
      },
    },
    title: {
      text: title,
      align: 'left',
    },
    subtitle: {
      text: subtitle,
      align: 'left',
    },
    exporting: {
      ...LINE_CHART_OPTIONS.exporting,
      enabled: isDownload,
    },
    xAxis: {
      categories: xAxisCategories,
      labels: {
        step: Math.ceil(xAxisCategories?.length / 10),
        showFirstLabel: true,
        showLastLabel: true,
      },
      gridLineColor: 'transparent',
      lineColor: 'transparent',
    },
    yAxis: {
      title: {
        text: '',
      },
      gridLineColor: COLOR_MAP[ChartElements.GridLine],
      labels: {
        formatter: function () {
          return `${formatNumberOnUnitMeasure(
            this.value,
            unitMeasure,
            indicatorData[0]?.decimals
          )}`
        },
      },
      plotLines: [
        {
          value: 0,
          color: COLOR_MAP[ChartElements.ZeroLine],
          zIndex: 1,
        },
      ],
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 480,
          },
          chartOptions: {
            xAxis: {
              labels: {
                step: Math.ceil(xAxisCategories?.length / 5),
                showFirstLabel: true,
                showLastLabel: true,
              },
            },
          },
        },
      ],
    },
    legend: {
      enabled: true,
      labelFormatter: function () {
        // Customizing the legend label to include additional text
        return `<b>${this.name}</b> ${
          this.userOptions.data.length === 0 ? '(No Data)' : ''
        }`
      },
      useHTML: true, // Enables HTML rendering in legend labels
    },
    series: lineData.map((line) => line.seriesData), // Add all series data to series array
  }
  applyAxisRange({
    visualizationMetadata: visualization,
    chartOptions: options,
    axisKey: 'yAxis',
    axisDimension: 'y',
  })
  return options
}
